import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, IconButton, Typography, TablePagination, TextField, useTheme, useMediaQuery, CardContent, Stack, InputAdornment, Tooltip
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import grupoMcpUsuarioService, { GrupoMcpUsuarioPagedResult } from '../../services/grupoMcpUsuarioService';
import grupoMcpService from '../../services/grupoMcpService';

const GrupoMcpUsuarioPage: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [data, setData] = useState<GrupoMcpUsuarioPagedResult>({
    items: [], totalCount: 0, pageNumber: 1, pageSize: 10, totalPages: 0
  });
  const [nombresGrupo, setNombresGrupo] = useState<Record<number, string>>({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchData = async (pageNumber: number, pageSize: number, search?: string) => {
    try {
      setLoading(true);
      const response = await grupoMcpUsuarioService.getGruposMcpUsuario({
        pageNumber,
        pageSize,
        searchTerm: search || undefined
      });
      setData(response);
      const gruposUnicos = response.items
        .map((i) => i.grupoMcp)
        .filter((v, idx, arr) => arr.indexOf(v) === idx);
      const map: Record<number, string> = {};
      await Promise.all(
        gruposUnicos.map(async (gm) => {
          try {
            const g = await grupoMcpService.getGrupoMcp(gm);
            map[gm] = g?.descripcion ?? '-';
          } catch {
            map[gm] = '-';
          }
        })
      );
      setNombresGrupo(map);
    } catch (error: any) {
      console.error('Error al cargar grupos de caja por usuario:', error);
      enqueueSnackbar('Error al cargar grupos de caja por usuario', { variant: 'error' });
      setData({ items: [], totalCount: 0, pageNumber: 1, pageSize, totalPages: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchData(page + 1, rowsPerPage, searchTerm);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [page, rowsPerPage, searchTerm]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDelete = async (grupoMcp: number, usuario: string) => {
    if (!window.confirm('¿Está seguro de eliminar esta asignación?')) return;
    try {
      await grupoMcpUsuarioService.delete(grupoMcp, usuario);
      enqueueSnackbar('Asignación eliminada exitosamente', { variant: 'success' });
      fetchData(page + 1, rowsPerPage, searchTerm);
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'Error al eliminar asignación', { variant: 'error' });
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Grupos de Caja por Usuario
            </Typography>
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => navigate('/sistema/grupo-caja-usuario/nuevo')}>
              Nueva Asignación
            </Button>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar por usuario..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#1976d2' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#f8f9fa' } }}
            />
          </Box>

          {loading ? (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
              <Typography color="text.secondary">Cargando...</Typography>
            </Box>
          ) : isMobile ? (
            <Stack spacing={2}>
              {data.items.map((item) => (
                <Card key={`${item.grupoMcp}-${item.usuario}`} variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Grupo {item.grupoMcp} - {nombresGrupo[item.grupoMcp] || '...'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Usuario: {item.usuario}
                    </Typography>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <IconButton size="small" onClick={() => handleDelete(item.grupoMcp, item.usuario)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Card>
              ))}
            </Stack>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Grupo de Caja</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Nombre de Grupo de Caja</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Usuario</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.items.map((item) => (
                    <TableRow key={`${item.grupoMcp}-${item.usuario}`} hover>
                      <TableCell>{item.grupoMcp}</TableCell>
                      <TableCell>{nombresGrupo[item.grupoMcp] ?? '...'}</TableCell>
                      <TableCell>{item.usuario}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Eliminar">
                          <IconButton size="small" onClick={() => handleDelete(item.grupoMcp, item.usuario)} color="error">
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={data.totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default GrupoMcpUsuarioPage;
